// src/components/discounts/DiscountQuiz.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';

const NUM_QUESTIONS = 3;
const TIME_LIMIT_SECONDS = 30;
const ATTEMPT_COOLDOWN_HOURS = 24;
const DISCOUNT_COUPONS = ['MEDQUIZ10', 'ANATOMY20', 'PHARMA5']; // Example coupons

const DiscountQuiz = () => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT_SECONDS);
  const [quizFinished, setQuizFinished] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const timer = useRef(null);
  const [canAttemptQuiz, setCanAttemptQuiz] = useState(true);
  const [cooldownMessage, setCooldownMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const lastAttemptTime = localStorage.getItem('discountQuizLastAttempt');
    const now = Date.now();

    if (lastAttemptTime && now - parseInt(lastAttemptTime) < ATTEMPT_COOLDOWN_HOURS * 60 * 60 * 1000) {
      setCanAttemptQuiz(false);
      const remainingTime = ATTEMPT_COOLDOWN_HOURS * 60 * 60 * 1000 - (now - parseInt(lastAttemptTime));
      const remainingHours = Math.ceil(remainingTime / (60 * 60 * 1000));
      setCooldownMessage(`You can attempt the quiz again in ${remainingHours} hours.`);
    }
  }, []);

  useEffect(() => {
    const fetchQuizQuestions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/data/quizQuestions.json');
        const data = await response.json();
        const shuffledQuestions = data.sort(() => 0.5 - Math.random());
        setQuestions(shuffledQuestions.slice(0, NUM_QUESTIONS));
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to load quiz questions.');
        setLoading(false);
      }
    };

    fetchQuizQuestions();
  }, []);

  const handleAnswerSubmit = useCallback(() => {
    if (!selectedAnswer) return;
    clearTimeout(timer.current);

    if (questions[currentQuestionIndex]?.correctAnswer) {
      if (selectedAnswer === questions[currentQuestionIndex].correctAnswer) {
        setScore(prevScore => prevScore + 1);
      }
    }

    setSelectedAnswer('');

    if (currentQuestionIndex < NUM_QUESTIONS - 1) {
      setCurrentQuestionIndex(prevIndex => prevIndex + 1);
      setTimeLeft(TIME_LIMIT_SECONDS);
    } else {
      setQuizFinished(true);
      localStorage.setItem('discountQuizLastAttempt', Date.now());
      if (score >= Math.ceil(NUM_QUESTIONS / 2) && DISCOUNT_COUPONS.length > 0) {
        const randomIndex = Math.floor(Math.random() * DISCOUNT_COUPONS.length);
        setCouponCode(DISCOUNT_COUPONS[randomIndex]);
      }
    }
  }, [selectedAnswer, currentQuestionIndex, questions, setScore, setSelectedAnswer, setCurrentQuestionIndex, setTimeLeft, setQuizFinished, score]); // Removed 'couponCode'

  useEffect(() => {
    if (!canAttemptQuiz || quizFinished || loading || questions.length === 0) {
      return;
    }

    if (timeLeft > 0) {
      timer.current = setTimeout(() => {
        setTimeLeft(prevTime => prevTime - 1);
      }, 1000);
    } else {
      handleAnswerSubmit();
    }

    return () => clearTimeout(timer.current);
  }, [timeLeft, currentQuestionIndex, canAttemptQuiz, quizFinished, loading, questions, handleAnswerSubmit]);

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 text-center">
        <p className="text-gray-600">Loading quiz questions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 text-center">
        <p className="text-red-500">Error: {error}</p>
      </div>
    );
  }

  if (!canAttemptQuiz) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 text-center">
        <h3 className="text-xl font-semibold text-gray-700 mb-4">Quiz Unavailable</h3>
        <p className="text-gray-600 mb-4">{cooldownMessage}</p>
      </div>
    );
  }

  if (quizFinished) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 text-center">
        <h3 className="text-xl font-semibold text-gray-700 mb-4">Quiz Finished!</h3>
        <p className="text-gray-600 mb-2">Your Score: {score} / {NUM_QUESTIONS}</p>
        {couponCode ? (
          <>
            <p className="text-green-500 font-semibold text-lg mb-2">Congratulations! You've earned a discount coupon:</p>
            <div className="bg-gray-200 rounded-md p-3 inline-block text-xl font-bold text-purple-700 mb-4">{couponCode}</div>
            <p className="text-gray-700 text-sm mb-2">Share this coupon with us on Instagram or WhatsApp to avail the discount!</p>
            <div className="flex justify-center gap-4">
              {/* Add your Instagram and WhatsApp sharing links here */}
              {/* Example: */}
              {/* <a href="https://instagram.com/your_handle" target="_blank" rel="noopener noreferrer" className="text-purple-500 hover:text-purple-700">Instagram</a> */}
              {/* <a href="https://wa.me/your_whatsapp_number" target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-700">WhatsApp</a> */}
            </div>
          </>
        ) : (
          <p className="text-gray-700">You did not qualify for a discount this time. Feel free to try again after 24 hours!</p>
        )}
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  if (!currentQuestion) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 text-center">
        <p className="text-gray-600">No questions available.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-xl font-semibold text-gray-700 mb-4">Quiz Time!</h3>
      <p className="text-gray-600 mb-2">Question {currentQuestionIndex + 1} / {NUM_QUESTIONS}</p>
      <p className="text-gray-600 mb-4">Time Left: {timeLeft} seconds</p>
      <p className="text-lg text-gray-800 mb-4">{currentQuestion.question}</p>
      <div className="space-y-2">
        {currentQuestion.options.map((option, index) => (
          <button
            key={index}
            className={`w-full text-left py-2 px-4 rounded-md border ${
              selectedAnswer === option ? 'bg-purple-100 border-purple-500' : 'border-gray-300 hover:bg-gray-100'
            }`}
            onClick={() => handleAnswerSelect(option)}
          >
            {option}
          </button>
        ))}
      </div>
      <button
        onClick={handleAnswerSubmit}
        className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-full mt-4 focus:outline-none focus:shadow-outline transition duration-300"
        disabled={!selectedAnswer}
      >
        Submit Answer
      </button>
    </div>
  );
};

export default DiscountQuiz;